import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, Chip, CircularProgress, Collapse, Divider, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { colors, alphaVar } from "../../ui/colors";
import { useClientJobsWithUnpaidEvents } from "./hooks/useClientJobsWithUnpaidEvents";
import { getJobEvents } from "../../jobs/jobs-list/jobs-layout/actions/getJobEvents";
import type { ClientJob, ClientJobEvent } from "./actions/getClientJobs";
import { sortJobEventsByStartDate, type JobEventForInvoice } from "../../jobs/jobs-details/events/board/context";
import type { TimeRangePreset } from "../../jobs/jobs-details/events/board/filters/job-event-filters.types";
import {
  TIME_FILTERS,
  TIME_SCHEME,
  type FilterColorScheme,
  type FilterOption,
} from "../../jobs/jobs-details/events/board/filters/job-event-filters.const";
import type { InvoiceVisitGroupSelection } from "../../invoice/create/types/invoiceVisitGroups";

/* ═══════════════════════════════════════════════════════════
   Inline FilterChip / FilterGroup (kept local to avoid coupling
   to the JobEventsController-specific version)
   ═══════════════════════════════════════════════════════════ */

function FilterChip({
  label,
  icon: Icon,
  active,
  onClick,
  scheme,
}: {
  label: string;
  icon: React.ComponentType<any>;
  active: boolean;
  onClick: () => void;
  scheme: FilterColorScheme;
}) {
  return (
    <Button
      onClick={onClick}
      disableRipple
      sx={{
        textTransform: "none",
        height: 30,
        px: 1.5,
        fontWeight: active ? 600 : 400,
        fontSize: 12,
        minWidth: "auto",
        borderRadius: "16px",
        backgroundColor: active ? scheme.activeBg : "transparent",
        border: `1px solid ${active ? scheme.activeBorder : "transparent"}`,
        color: active ? scheme.activeText : "text.secondary",
        transition: "all 120ms ease-in-out",
        "&:hover": {
          backgroundColor: active ? scheme.activeBg : scheme.hoverBg,
          border: `1px solid ${active ? scheme.activeBorder : scheme.hoverBorder}`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Icon sx={{ fontSize: 16, color: active ? scheme.activeIcon : "text.disabled", transition: "color 120ms ease" }} />
        {label}
      </Box>
    </Button>
  );
}

function FilterGroup<V extends string>({
  options,
  value,
  onChange,
  scheme,
}: {
  options: readonly FilterOption<V>[];
  value: V | undefined;
  onChange: (next: V | undefined) => void;
  scheme: FilterColorScheme;
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {options.map((opt) => (
        <FilterChip
          key={opt.value}
          label={opt.label}
          icon={opt.icon}
          active={value === opt.value}
          onClick={() => onChange(value === opt.value ? undefined : opt.value)}
          scheme={scheme}
        />
      ))}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════
   Date range computation (reused from JobsEvents logic)
   ═══════════════════════════════════════════════════════════ */

function computeDateRange(preset?: TimeRangePreset, customStart?: string, customEnd?: string) {
  if (!preset) return {};
  const now = new Date();

  if (preset === "THIS_WEEK") {
    const day = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((day + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return { startDate: monday.toISOString(), endDate: sunday.toISOString() };
  }
  if (preset === "THIS_MONTH") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }
  if (preset === "NEXT_MONTH") {
    const start = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 2, 0, 23, 59, 59, 999);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }
  if (preset === "CUSTOM") {
    return {
      startDate: customStart ? new Date(`${customStart}T00:00:00`).toISOString() : undefined,
      endDate: customEnd ? new Date(`${customEnd}T23:59:59.999`).toISOString() : undefined,
    };
  }
  return {};
}

/* ═══════════════════════════════════════════════════════════
   JobEventRows – sub-table inside an expanded job row
   ═══════════════════════════════════════════════════════════ */

function JobEventRows({
  job,
  startDate,
  endDate,
  selectedUids,
  onToggle,
}: {
  job: ClientJob;
  startDate?: string;
  endDate?: string;
  selectedUids: Set<string>;
  onToggle: (event: JobEventForInvoice, job: ClientJob) => void;
}) {
  const [events, setEvents] = useState<ClientJobEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // get UNBILLED events
    getJobEvents({ jobId: job.id, pageSize: 200, startDate, endDate, billingStatus: "UNBILLED" })
      .then((res: any) => {
        if (!cancelled) setEvents(res.items ?? []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [job.id, startDate, endDate]);

  if (loading) {
    return (
      <TableRow>
        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
          <CircularProgress size={20} thickness={5} sx={{ color: colors.orange[400] }} />
        </TableCell>
      </TableRow>
    );
  }

  if (!events.length) {
    return (
      <TableRow>
        <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No visits in this date range
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {events.map((ev) => {
        const checked = selectedUids.has(ev.uid);
        const asInvoiceEvent: JobEventForInvoice = {
          uid: ev.uid,
          startDate: ev.startDate,
          endDate: ev.endDate,
          status: ev.status,
        };
        return (
          <TableRow
            key={ev.uid}
            hover
            onClick={() => onToggle(asInvoiceEvent, job)}
            sx={{ cursor: "pointer", "& td": { borderBottom: "1px solid", borderColor: "divider" } }}
          >
            <TableCell padding="checkbox">
              <Checkbox size="small" checked={checked} sx={{ color: colors.orange[300], "&.Mui-checked": { color: colors.orange[500] } }} />
            </TableCell>
            <TableCell sx={{ fontSize: "0.8125rem" }}>
              {new Date(ev.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </TableCell>
            <TableCell sx={{ fontSize: "0.8125rem" }}>
              {new Date(ev.startDate).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
              {" - "}
              {new Date(ev.endDate).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
            </TableCell>
            <TableCell>
              <Chip
                label={ev.status}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  borderRadius: "6px",
                }}
              />
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   JobRow – expandable table row for a single job
   ═══════════════════════════════════════════════════════════ */

function formatJobType(type?: string) {
  if (!type) return "";
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function JobRow({
  job,
  startDate,
  endDate,
  selectedUids,
  onToggleEvent,
}: {
  job: ClientJob;
  startDate?: string;
  endDate?: string;
  selectedUids: Set<string>;
  onToggleEvent: (event: JobEventForInvoice, job: ClientJob) => void;
}) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const unpaidCount = job.events?.filter((e) => !e.billingInvoiceUid).length ?? 0;
  const jobLabel = job.alias ? `#${job.id} - ${job.alias}` : `#${job.id}`;

  return (
    <>
      <TableRow hover onClick={() => setOpen(!open)} sx={{ cursor: "pointer", "& > td": { borderBottom: "none" } }}>
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small">{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", lineHeight: 1.3 }}>{jobLabel}</Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", mt: 0.25 }}>
            {formatJobType(job.type)}
            {job.isRepetitive ? " / Recurring" : " / One-time"}
          </Typography>
        </TableCell>
        <TableCell sx={{ fontSize: "0.8125rem", color: theme.palette.text.secondary }}>
          <Chip
            label={job.status?.replace(/_/g, " ")}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.6rem",
              fontWeight: 700,
              textTransform: "uppercase",
              borderRadius: "6px",
              bgcolor: alphaVar(colors.blue[400], 0.08),
              color: colors.blue[600],
              border: `1px solid ${alphaVar(colors.blue[400], 0.15)}`,
            }}
          />
        </TableCell>
        <TableCell align="right">
          <Chip
            label={`${unpaidCount} unpaid`}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.625rem",
              fontWeight: 800,
              bgcolor: alphaVar(colors.orange[400], 0.1),
              color: colors.orange[700],
              border: `1px solid ${alphaVar(colors.orange[400], 0.2)}`,
              borderRadius: "6px",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
            }}
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, px: 0, borderBottom: "1px solid", borderColor: "divider" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ bgcolor: alphaVar(theme.palette.grey[50], 0.5), px: 2, py: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase", color: "text.secondary" }}>Date</TableCell>
                    <TableCell sx={{ fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase", color: "text.secondary" }}>Time</TableCell>
                    <TableCell sx={{ fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase", color: "text.secondary" }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <JobEventRows job={job} startDate={startDate} endDate={endDate} selectedUids={selectedUids} onToggle={onToggleEvent} />
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   JobSelectionForInvoice – main orchestrator
   ═══════════════════════════════════════════════════════════ */

type SelectedEventEntry = { event: JobEventForInvoice; job: ClientJob };

function formatJobGroupTitle(job: ClientJob) {
  return job.alias ? `Job #${job.id} — ${job.alias}` : `Job #${job.id}`;
}

function groupSelectedVisitsByJob(selected: Map<string, SelectedEventEntry>): { job: ClientJob; entries: SelectedEventEntry[] }[] {
  const byJob = new Map<number, { job: ClientJob; entries: SelectedEventEntry[] }>();
  selected.forEach((entry) => {
    const id = entry.job.id;
    const cur = byJob.get(id) ?? { job: entry.job, entries: [] as SelectedEventEntry[] };
    cur.entries.push(entry);
    byJob.set(id, cur);
  });
  return Array.from(byJob.values())
    .map((g) => {
      const sortedEvents = sortJobEventsByStartDate(g.entries.map((e) => ({ ...e.event })));
      const uidToEntry = new Map(g.entries.map((e) => [e.event.uid, e]));
      const entries = sortedEvents.map((ev) => uidToEntry.get(ev.uid)!).filter(Boolean) as SelectedEventEntry[];
      return { job: g.job, entries };
    })
    .sort((a, b) => a.job.id - b.job.id);
}

interface Props {
  clientUid: string;
  /** `job` is the primary job (earliest selected visit): scope of work & scheduler for the editor. */
  onContinue: (events: JobEventForInvoice[], job: ClientJob, visitGroups: InvoiceVisitGroupSelection[]) => void;
}

export function JobSelectionForInvoice({ clientUid, onContinue }: Props) {
  const theme = useTheme();
  const { data: jobs, isLoading } = useClientJobsWithUnpaidEvents({ clientUid });

  const [selectedEvents, setSelectedEvents] = useState<Map<string, SelectedEventEntry>>(new Map());
  const [timeRange, setTimeRange] = useState<TimeRangePreset | undefined>(undefined);
  const [customStartDate, setCustomStartDate] = useState<string | undefined>(undefined);
  const [customEndDate, setCustomEndDate] = useState<string | undefined>(undefined);

  const { startDate, endDate } = useMemo(
    () => computeDateRange(timeRange, customStartDate, customEndDate),
    [timeRange, customStartDate, customEndDate],
  );

  const selectedUids = useMemo(() => new Set(selectedEvents.keys()), [selectedEvents]);

  const toggleEvent = useCallback((event: JobEventForInvoice, job: ClientJob) => {
    setSelectedEvents((prev) => {
      const next = new Map(prev);
      if (next.has(event.uid)) {
        next.delete(event.uid);
      } else {
        next.set(event.uid, { event, job });
      }
      return next;
    });
  }, []);

  const handleTimeChange = useCallback((val: TimeRangePreset | undefined) => {
    setTimeRange(val);
    if (val !== "CUSTOM") {
      setCustomStartDate(undefined);
      setCustomEndDate(undefined);
    }
  }, []);

  const handleContinue = useCallback(() => {
    if (!selectedEvents.size) return;

    const entries = Array.from(selectedEvents.values());
    const eventsArray = entries.map((e) => e.event);
    const sorted = sortJobEventsByStartDate(eventsArray.map((e) => ({ ...e })));
    const jobByEventUid = new Map(entries.map((e) => [e.event.uid, e.job]));
    const primaryJob = jobByEventUid.get(sorted[0].uid);
    if (!primaryJob) return;

    const visitGroups: InvoiceVisitGroupSelection[] = groupSelectedVisitsByJob(selectedEvents).map((g) => ({
      jobId: g.job.id,
      jobLabel: formatJobGroupTitle(g.job),
      eventUids: g.entries.map((e) => e.event.uid),
    }));

    onContinue(sorted, primaryJob, visitGroups);
  }, [selectedEvents, onContinue]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem", mb: 0.5 }}>
          Select visits to invoice
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
          Expand a job to select unpaid visits. You can select visits across multiple jobs.
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ px: 3, pb: 2 }}>
        <FilterGroup options={TIME_FILTERS} value={timeRange} onChange={handleTimeChange} scheme={TIME_SCHEME} />
        {timeRange === "CUSTOM" && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
            <TextField
              type="date"
              size="small"
              label="From"
              value={customStartDate ?? ""}
              onChange={(e) => setCustomStartDate(e.target.value || undefined)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, "& .MuiInputBase-input": { fontSize: 12, py: 0.75 } }}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              value={customEndDate ?? ""}
              onChange={(e) => setCustomEndDate(e.target.value || undefined)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, "& .MuiInputBase-input": { fontSize: 12, py: 0.75 } }}
            />
          </Box>
        )}
      </Box>

      <Divider />

      {/* Jobs table */}
      <TableContainer sx={{ flex: 1, overflow: "auto" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
            <CircularProgress size={32} thickness={5} sx={{ color: colors.orange[400] }} />
          </Box>
        ) : !jobs?.length ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, gap: 1 }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
            <Typography variant="body2" color="text.secondary">
              No jobs with unpaid visits
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase", color: "text.secondary" }}>Job</TableCell>
                <TableCell sx={{ fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase", color: "text.secondary" }}>Status</TableCell>
                <TableCell align="right" sx={{ fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase", color: "text.secondary" }}>
                  Unpaid Visits
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <JobRow key={job.id} job={job} startDate={startDate} endDate={endDate} selectedUids={selectedUids} onToggleEvent={toggleEvent} />
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
          {selectedEvents.size > 0 ? `${selectedEvents.size} visit${selectedEvents.size > 1 ? "s" : ""} selected` : "No visits selected"}
        </Typography>
        <Button
          variant="contained"
          disabled={selectedEvents.size === 0}
          onClick={handleContinue}
          disableElevation
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8125rem",
            borderRadius: "10px",
            px: 3,
            bgcolor: colors.orange[500],
            "&:hover": { bgcolor: colors.orange[600] },
            "&.Mui-disabled": { bgcolor: alphaVar(colors.orange[300], 0.3) },
          }}
        >
          Continue to Invoice ({selectedEvents.size})
        </Button>
      </Box>
    </Box>
  );
}
